import { useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Mail, Download, Printer, Copy, ArrowUpRight, BookOpen, Package, Mic, MapPin,
} from "lucide-react";
import { CURRICULUM } from "@/lib/curriculum";
import { LEGAL } from "@/lib/legal";

const BOOKING_EMAIL = LEGAL.email;

const BOOKING_HREF =
  `mailto:${BOOKING_EMAIL}` +
  `?subject=${encodeURIComponent("Booking request — Rich Culmer / T.A.C.C.")}` +
  `&body=${encodeURIComponent(
    "Hi Rich,\n\nWe'd love to have you on our show.\n\n" +
    "Show / Outlet:\nAudience size:\nProposed date & time:\nFormat (in-studio / remote / phone):\nTopic focus:\nSegment length:\n\n" +
    "Looking forward,\n"
  )}`;

const FACTS = [
  { k: "Name", v: "Rich Culmer" },
  { k: "Title", v: "Founder & Healer, T.A.C.C." },
  { k: "Brand", v: "Through Adversity Comes Change" },
  { k: "Based in", v: "New York, USA" },
  { k: "Body of work", v: "3 books · 8 modules · 1 formula" },
  { k: "In development", v: "10 years, tested on real shifts" },
];

const TALKING_POINTS = [
  {
    tag: "The formula",
    title: "Through Adversity Comes Change.",
    body: "How the worst season of a person's life becomes the exact preparation for their purpose — and why you go through pain, never around it.",
  },
  {
    tag: "Faith",
    title: "God is love — the foundation of real healing.",
    body: "Why healing that lasts starts with facing God and yourself honestly, and how that reframes everything a person has carried.",
  },
  {
    tag: "The wall",
    title: "The Wall of Adversity.",
    body: "The fears, traumas and hidden pain only you and God know about. You can't go over it, under it, or around it. Rich walks people straight through.",
  },
  {
    tag: "Workforce",
    title: "Let people heal before the shift.",
    body: "Corporate wellness that respects the weight workers already carry — pre-shift grounding for healthcare, first responders, hospitality and call centers.",
  },
  {
    tag: "The turn",
    title: "Becoming the healer.",
    body: "How a private survival notebook became a formula, three books and eight modules — and how anyone's pain can become someone else's rescue.",
  },
];

export default function PressKitPage() {
  useEffect(() => {
    document.title = "Press Kit · Rich Culmer — T.A.C.C.";
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta");
      m.name = "description";
      document.head.appendChild(m);
      return m;
    })();
    const prev = meta.getAttribute("content");
    meta.setAttribute(
      "content",
      "Media kit for Rich Culmer, founder of T.A.C.C. — Through Adversity Comes Change. Bio, headshot, the eight module titles, talking points, and one-tap booking for producers."
    );
    return () => { if (prev) meta.setAttribute("content", prev); };
  }, []);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(BOOKING_EMAIL);
      toast.success("Email copied — " + BOOKING_EMAIL);
    } catch {
      toast.error("Copy failed. Email: " + BOOKING_EMAIL);
    }
  };

  return (
    <main className="pt-32 md:pt-40 pb-28" data-testid="press-kit-page">
      {/* Hero */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.65, 0, 0.35, 1] }}
          className="grid grid-cols-12 gap-y-10 gap-x-8 items-end"
        >
          <div className="col-span-12 lg:col-span-8">
            <div className="section-tag mb-6">§ Press kit · For producers &amp; media</div>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.88] tracking-tighter">
              Book <span className="italic text-[#B45309]">Rich Culmer</span>.
            </h1>
            <p className="text-[#78716C] text-lg leading-relaxed mt-8 max-w-2xl">
              Founder of T.A.C.C. — Through Adversity Comes Change. A faith-led healer who
              turns lived pain into a repeatable formula: face it, go through it, and become
              the one who helps others heal. Everything a segment producer needs is on this page.
            </p>
            <div className="flex flex-wrap gap-3 mt-10">
              <a
                href={BOOKING_HREF}
                data-testid="press-book-btn"
                className="btn-ember text-sm"
              >
                <Mail size={16} /> Book Rich in one tap
              </a>
              <a
                href="/author/avatar.jpg"
                download="rich-culmer-headshot.jpg"
                data-testid="press-download-headshot"
                className="btn-outline text-sm"
              >
                <Download size={16} /> Download headshot
              </a>
              <button
                onClick={() => window.print()}
                data-testid="press-print-btn"
                className="btn-outline text-sm"
              >
                <Printer size={16} /> Save one-pager
              </button>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-4">
            <div className="relative aspect-square overflow-hidden border border-[#E7DFD5] bg-black">
              <img
                src="/author/avatar.jpg"
                alt="Rich Culmer — founder of T.A.C.C., available for interviews"
                data-testid="press-headshot"
                className="absolute inset-0 w-full h-full object-cover object-top contrast-[1.05]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <div className="font-display text-2xl">Rich Culmer.</div>
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#B45309] mt-1">
                  High-res · print &amp; broadcast ready
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Quick facts */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 mt-24 md:mt-32">
        <div className="section-tag mb-8">§ At a glance</div>
        <div className="grid grid-cols-2 md:grid-cols-3 border-t border-l border-[#E7DFD5]" data-testid="press-facts">
          {FACTS.map((f) => (
            <div key={f.k} className="border-b border-r border-[#E7DFD5] p-6 md:p-8">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C] mb-3">{f.k}</div>
              <div className="font-display text-2xl md:text-3xl leading-tight tracking-tight">{f.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Bio */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 mt-24 md:mt-32">
        <div className="grid grid-cols-12 gap-y-10 gap-x-8">
          <div className="col-span-12 md:col-span-4">
            <div className="section-tag mb-6">§ Bio</div>
            <h2 className="font-display text-4xl md:text-5xl leading-[0.95] tracking-tight">
              The <span className="italic text-[#B45309]">healer</span> behind the formula.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-7 md:col-start-6 space-y-5 text-[#78716C] text-lg leading-relaxed" data-testid="press-bio">
            <p>
              Rich Culmer is the founder of T.A.C.C. — Through Adversity Comes Change — a faith-led
              healing method built not in a classroom, but on the worst mornings of a working life.
              What began as a private notebook of pre-shift rituals became a decade-long study of
              what breaks people, and what brings them back.
            </p>
            <p>
              Over ten years Rich turned that lived experience into three books, eight narrated
              modules, and a corporate wellness practice for companies that stopped pretending their
              people were fine. His message is direct and uncompromising: God is love, you cannot go
              around your pain, and the thing that nearly broke you was preparing you to heal others.
            </p>
            <p>
              He speaks with the authority of someone who went through the wall himself — and now
              hands people a formula to do the same.
            </p>
            <p className="text-[#A8A29E] text-sm font-mono tracking-wide pt-2">
              Short bio: Rich Culmer is the founder of T.A.C.C. (Through Adversity Comes Change), a
              faith-led healing course of three books and eight modules built over ten years to help
              workers face their pain and become healers themselves.
            </p>
          </div>
        </div>
      </section>

      {/* The 8 modules */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 mt-24 md:mt-32">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div className="section-tag mb-6">§ The eight modules</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight max-w-2xl">
              Five principles. Three articles. <span className="italic text-[#B45309]">One journey.</span>
            </h2>
          </div>
          <a href="/modules" className="btn-outline text-sm" data-testid="press-modules-link">
            See the modules <ArrowUpRight size={16} />
          </a>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-[#E7DFD5]" data-testid="press-modules">
          {CURRICULUM.map((m) => (
            <div
              key={m.id}
              className="group border-b border-r border-[#E7DFD5] p-6 md:p-8 flex items-start gap-5 transition-colors duration-300 hover:bg-black/5"
              data-testid={`press-module-${m.id}`}
            >
              <span className="font-display text-4xl md:text-5xl text-[#B45309]/80 leading-none">{m.n}</span>
              <div>
                <h3 className="font-display text-2xl md:text-3xl leading-tight tracking-tight">{m.title}</h3>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#78716C] mt-2">{m.focus}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Talking points */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 mt-24 md:mt-32">
        <div className="section-tag mb-6 flex items-center gap-2"><Mic size={13} className="text-[#B45309]" /> § What Rich can speak to</div>
        <div className="grid grid-cols-12 gap-4 md:gap-6" data-testid="press-talking-points">
          {TALKING_POINTS.map((t, i) => (
            <article
              key={t.title}
              className={`tacc-card p-8 md:p-10 flex flex-col col-span-12 ${i === 0 ? "md:col-span-6" : "md:col-span-6 lg:col-span-4"}`}
              data-testid={`press-topic-${i}`}
            >
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#B45309] mb-4">{t.tag}</span>
              <h3 className="font-display text-2xl md:text-3xl leading-tight tracking-tight mb-3">{t.title}</h3>
              <p className="text-[#78716C] leading-relaxed">{t.body}</p>
            </article>
          ))}
          <article className="tacc-card p-8 md:p-10 flex flex-col justify-center col-span-12 lg:col-span-4 border-[#F59E0B]/40">
            <div className="flex gap-6 text-[#78716C] text-sm">
              <div className="flex items-center gap-2"><BookOpen size={16} className="text-[#B45309]" /> 3 books</div>
              <div className="flex items-center gap-2"><Package size={16} className="text-[#B45309]" /> 8 modules</div>
            </div>
            <p className="text-[#78716C] leading-relaxed mt-5">
              Formats: in-studio, remote, or phone. Available for interviews, panels, and live
              healing segments. Warm, plain-spoken, and unafraid of the hard questions.
            </p>
          </article>
        </div>
      </section>

      {/* Booking CTA */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 mt-24 md:mt-32">
        <div className="tacc-card p-10 md:p-16 relative overflow-hidden" data-testid="press-booking">
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#F59E0B]/10 blur-3xl pointer-events-none" />
          <div className="section-tag mb-6 flex items-center gap-2"><MapPin size={13} className="text-[#B45309]" /> § Booking</div>
          <h2 className="font-display text-4xl md:text-6xl leading-[0.92] tracking-tight max-w-3xl">
            One tap and Rich is <span className="italic text-[#B45309]">in your inbox</span>.
          </h2>
          <p className="text-[#78716C] text-lg leading-relaxed mt-6 max-w-xl">
            Press the button and a booking email opens, pre-filled and ready. Or copy the address
            and reach out however you like.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-10">
            <a href={BOOKING_HREF} data-testid="press-booking-btn" className="btn-ember text-base !px-8 !py-4">
              <Mail size={18} /> Book Rich Culmer
            </a>
            <button onClick={copyEmail} data-testid="press-copy-email" className="btn-outline text-sm">
              <Copy size={16} /> {BOOKING_EMAIL}
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}
